/**
 * Read-receipt ("opened by the recipient") state of a mail WE sent with open tracking on.
 * Pure helper shared by the mail list row and the thread screen (mirrors the admin
 * `mailReadReceiptUtils`).
 *
 * @module helpers/mailReadReceipt
 */

const toTime = (value) => {
    const time = new Date(value || 0).getTime();

    return Number.isFinite(time) && time > 0 ? time : null;
};

/**
 * Derives the receipt from a normalized message. Prefers the server summary (`tracking.isRead`,
 * `readAt`, `lastReadAt`) and falls back to the raw `tracking.opens` buckets, so a backend that
 * predates the summary fields still lights the indicator up. Prefetch-only hits (Apple Mail
 * Privacy Protection, link scanners) are never a read.
 *
 * @param {Object} message Normalized mail message (`direction`, `folderKey`, `status`, `tracking`).
 * @returns {{state: ('read'|'unread'|'privacy'), firstOpenedAt: (string|null),
 *          lastOpenedAt: (string|null), openCount: number}|null} null when the message is not an
 *          outgoing tracked mail, or is still queued / failed (nothing to show).
 */
export function resolveReadReceipt(message) {
    const tracking = message?.tracking;
    const outgoing = message?.direction === 'outgoing' || message?.folderKey === 'sent';

    if (!tracking || !outgoing || message?.status === 'queued' || message?.status === 'failed') {
        return null;
    }

    const opens = Array.isArray(tracking.opens) ? tracking.opens : [];
    const confirmed = opens.filter((open) => open && !open.prefetchOnly && toTime(open.firstOpenedAt));
    const firsts = confirmed.map((open) => toTime(open.firstOpenedAt));
    const lasts = confirmed.map((open) => toTime(open.lastOpenedAt || open.firstOpenedAt));

    if (tracking.isRead === true || confirmed.length > 0) {
        const first = tracking.readAt || (firsts.length ? new Date(Math.min(...firsts)).toISOString() : null);

        return {
            state: 'read',
            firstOpenedAt: first,
            lastOpenedAt: tracking.lastReadAt || (lasts.length ? new Date(Math.max(...lasts)).toISOString() : first),
            openCount: Number(tracking.readCount) || confirmed.length || 1,
        };
    }

    if (tracking.enabled !== true) {
        return null;
    }

    return {
        state: tracking.privacyProtected === true ? 'privacy' : 'unread',
        firstOpenedAt: null,
        lastOpenedAt: null,
        openCount: 0,
    };
}
